import { useEffect, useMemo, useState } from 'react';
import type { Meal, WeeklyPlan } from '../types';
import { MEAL_LABELS } from '../types';
import { formatShortDate, weekdayLabel } from '../lib/date';
import { AlertIcon, CheckIcon } from './Icons';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  plan: WeeklyPlan;
  onClose: () => void;
  onApply: (plan: WeeklyPlan) => void;
}

type MealServiceDraft = Record<string, Record<Meal, boolean>>;

const MEALS: Meal[] = ['breakfast', 'lunch'];

function mealServiceFor(plan: WeeklyPlan): MealServiceDraft {
  return Object.fromEntries(plan.days.map((day) => [
    day.date,
    {
      breakfast: day.mealService?.breakfast ?? day.serviceEnabled,
      lunch: day.mealService?.lunch ?? day.serviceEnabled,
    },
  ]));
}

function manualServiceLabel(service: Record<Meal, boolean>): string {
  if (service.breakfast && service.lunch) return '手动设置：全天供餐';
  if (!service.breakfast && !service.lunch) return '手动设置：全天停餐';
  return `手动设置：${service.breakfast ? '早餐供餐' : '中午供餐'}`;
}

export function WeeklyServiceSettings({ open, plan, onClose, onApply }: Props) {
  const [draft, setDraft] = useState<MealServiceDraft>(() => mealServiceFor(plan));

  useEffect(() => {
    if (open) setDraft(mealServiceFor(plan));
  }, [open, plan.weekStart]);

  const mealsToClear = useMemo(() => plan.days.flatMap((day) => MEALS.flatMap((meal) => {
    const selectedCount = day.selections.filter((selection) => selection.meal === meal).length;
    if (draft[day.date]?.[meal] !== false || selectedCount === 0) return [];
    return [{ date: day.date, meal, selectedCount }];
  })), [draft, plan.days]);

  const toggleMeal = (date: string, meal: Meal) => {
    setDraft((current) => ({
      ...current,
      [date]: {
        ...current[date],
        [meal]: !(current[date]?.[meal] ?? true),
      },
    }));
  };

  const apply = () => {
    const days = plan.days.map((day) => {
      const original = {
        breakfast: day.mealService?.breakfast ?? day.serviceEnabled,
        lunch: day.mealService?.lunch ?? day.serviceEnabled,
      };
      const nextService = draft[day.date] ?? original;
      const selections = day.selections.filter((selection) => nextService[selection.meal]);
      const settingsChanged = MEALS.some((meal) => nextService[meal] !== original[meal]);
      const selectionsChanged = selections.length !== day.selections.length;

      if (!settingsChanged && !selectionsChanged) return day;
      return {
        ...day,
        mealService: { ...nextService },
        serviceEnabled: nextService.breakfast || nextService.lunch,
        source: 'manual' as const,
        label: manualServiceLabel(nextService),
        selections,
      };
    });

    onApply({ ...plan, status: 'draft', days });
    onClose();
  };

  return (
    <Modal open={open} title="本周供餐设置" eyebrow="按餐次统一编排" size="large" onClose={onClose}>
      <div className="weekly-service-settings">
        <p className="weekly-service-settings__intro">
          工作日默认供应早餐和中午，非工作日默认停餐。只有本周有变化时才需要调整。
        </p>

        <div className="weekly-service-settings__days" role="list" aria-label="本周各餐次供餐设置">
          {plan.days.map((day) => (
            <section className="weekly-service-day" key={day.date} role="listitem">
              <header className="weekly-service-day__heading">
                <strong>{formatShortDate(day.date)}</strong>
                <span>{weekdayLabel(day.date)}</span>
              </header>
              <div className="weekly-service-day__meals">
                {MEALS.map((meal) => {
                  const enabled = draft[day.date]?.[meal] ?? day.mealService?.[meal] ?? day.serviceEnabled;
                  return (
                    <button
                      className={`meal-service-switch ${enabled ? 'is-on' : 'is-off'}`}
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      aria-label={`设置${formatShortDate(day.date)} ${weekdayLabel(day.date)}${MEAL_LABELS[meal]}${enabled ? '停餐' : '供餐'}`}
                      key={meal}
                      onClick={() => toggleMeal(day.date, meal)}
                    >
                      <span className="meal-service-switch__label">{MEAL_LABELS[meal]}</span>
                      <span className="meal-service-switch__state">
                        {enabled && <CheckIcon size={18} />}
                        {enabled ? '供餐' : '停餐'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {mealsToClear.length > 0 && (
          <div className="weekly-service-settings__warning" role="alert">
            <AlertIcon size={22} />
            <div>
              <strong>以下已选菜品会在保存后清空</strong>
              <ul>
                {mealsToClear.map((item) => (
                  <li key={`${item.date}-${item.meal}`}>
                    {formatShortDate(item.date)} {weekdayLabel(item.date)} · {MEAL_LABELS[item.meal]}：将清除 {item.selectedCount} 道已选菜品
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <footer className="weekly-service-settings__actions">
          <button className="button button--ghost" type="button" onClick={onClose}>取消</button>
          <button className="button button--primary" type="button" onClick={apply}>保存本周供餐设置</button>
        </footer>
      </div>
    </Modal>
  );
}
